import React, { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import "./TeamsChat.css";
import { ArrowUpRight16Filled } from "@fluentui/react-icons";
import { useStandalonePwa } from "../hooks/useStandalonePwa";
import { useNestedScrollChain } from "../hooks/useNestedScrollChain";

function TeamsChat({ projectData }) {
  const { instance, accounts } = useMsal();
  const isStandalonePwa = useStandalonePwa();
  const listRef = useRef(null);

  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const latestOpportunityRef = useRef(null);

  useNestedScrollChain(listRef, isStandalonePwa && !loading && Boolean(selectedChat));

  /* ------------------------------------------------------------------ */
  /*  Acquire an access-token and look for a matching chat on mount      */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (accounts.length === 0) return;

    (async () => {
      try {
        setLoading(true);
        const { accessToken } = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        await fetchChats(accessToken, projectData?.opportunityNo);
      } catch (err) {
        console.error("Error acquiring token for Teams chat:", err);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, instance]);

  // Reload chats whenever the selected project changes
  useEffect(() => {
    if (accounts.length === 0) return;

    // Clear previous chat data immediately to avoid showing stale info
    setSelectedChat(null);
    setMessages([]);

    if (!projectData?.opportunityNo) {
      return;
    }

    latestOpportunityRef.current = projectData.opportunityNo;

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const { accessToken } = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        if (!cancelled) {
          await fetchChats(accessToken, projectData.opportunityNo);
        }
      } catch (err) {
        console.error("Error loading Teams chat for project:", err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectData?.opportunityNo, accounts, instance]);

  // Add effect to handle refresh events
  useEffect(() => {
    const handleRefresh = async () => {
      if (accounts.length === 0) return;

      try {
        const { accessToken } = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        await fetchChats(accessToken, latestOpportunityRef.current || projectData?.opportunityNo);
      } catch (err) {
        console.error("Error refreshing Teams chat:", err);
      }
    };

    window.addEventListener('refreshTeamsChat', handleRefresh);
    return () => window.removeEventListener('refreshTeamsChat', handleRefresh);
  }, [accounts, instance]);

  /* ------------------------------ Helpers --------------------------- */
  /** Get the user's chats and pick the first one that matches */
  const fetchChats = async (accessToken, opportunityNo) => {
    if (!opportunityNo) return;
    try {
      setLoading(true);
      let endpoint = "https://graph.microsoft.com/v1.0/me/chats";
      let matchingChat = null;

      while (endpoint && !matchingChat) {
        const res = await fetch(endpoint, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        if (!res.ok) {
          console.error("Failed to fetch chats:", await res.json());
          return;
        }

        const data = await res.json();
        matchingChat = (data.value || []).find((chat) =>
          chat.topic?.includes(opportunityNo)
        );

        endpoint = data["@odata.nextLink"] || null;
      }

      if (matchingChat) {
        if (latestOpportunityRef.current !== opportunityNo) {
          return;
        }
        setSelectedChat(matchingChat);
        await fetchChatMessages(accessToken, matchingChat.id, opportunityNo);
      } else {
        if (latestOpportunityRef.current === opportunityNo) {
          setSelectedChat(null);
          setMessages([]);
        }
      }
    } catch (err) {
      console.error("Error fetching chats:", err);
    } finally {
      setLoading(false);
    }
  };


  /** Load latest valid user messages (limited) */
  const fetchChatMessages = async (accessToken, chatId, opportunityNo) => {
    const MAX_MESSAGES = 10;   // <-- display limit
    const MAX_PAGES = 20;      // safety limit

    try {
      setLoading(true);

      const allMessages = [];
      let url =
        `https://graph.microsoft.com/v1.0/chats/${chatId}/messages` +
        `?$top=50&$orderby=createdDateTime desc`;

      let pageCount = 0;

      // Keep paging UNTIL we collect enough REAL messages
      while (
        url &&
        pageCount < MAX_PAGES &&
        allMessages.length < MAX_MESSAGES
      ) {
        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Prefer: "include-unknown-enum-members",
          },
        });

        if (!res.ok) {
          console.error("Failed to fetch messages:", await res.json());
          break;
        }

        const data = await res.json();

        if (process.env.NODE_ENV === "development") {
          console.log("[Teams Chat] Page:", pageCount + 1, {
            messages: (data.value || []).length,
            validSoFar: allMessages.length,
            hasNext: !!data["@odata.nextLink"],
          });
        }

        const page = (data.value || [])
          .filter((m) => {
            const isSystem = m.messageType === "systemEventMessage";
            const hasUser = !!m.from?.user?.displayName;
            const hasText = !!m.body?.content?.trim();
            const hasAttachments = (m.attachments?.length ?? 0) > 0;

            return !isSystem && hasUser && (hasText || hasAttachments);
          })
          .map((m) => {
            let content = m.body?.content?.trim();

            if (!content && m.attachments?.length) {
              content = `<em>Attachment${m.attachments.length > 1
                  ? `s (${m.attachments.length})`
                  : ""
                }</em>`;
            }

            return {
              id: m.id,
              sender: m.from.user.displayName,
              created: m.createdDateTime,
              content,
            };
          });

        allMessages.push(...page);

        // Stop paging once enough messages collected
        url =
          allMessages.length < MAX_MESSAGES
            ? data["@odata.nextLink"] || null
            : null;

        pageCount++;
      }

      // Final safety slice
      const limitedMessages = allMessages.slice(0, MAX_MESSAGES);

      if (latestOpportunityRef.current === opportunityNo) {
        setMessages(limitedMessages);
      }
    } catch (err) {
      console.error("Error fetching chat messages:", err);
    } finally {
      setLoading(false);
    }
  };


  /* ----------------------------- Render ----------------------------- */
  return (
    <section className="teams-chat p-4">
      <div className="teams-chat-header flex justify-between items-center mb-4">
        <h2 className="teams-chat-title text-xl font-semibold">Teams Chat</h2>
        {selectedChat && (
          <a
            className="teams-chat-view-all flex items-center gap-2 text-blue-600 hover:underline"
            href={selectedChat.webUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            View All <ArrowUpRight16Filled />
          </a>
        )}
      </div>

      {loading ? (
        <div className="teams-chat-loading flex items-center justify-center h-[330px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : selectedChat ? (
        <>
          <div className="teams-chat-group mb-3">
            <h3 className="teams-chat-group-name text-sm text-gray-600">Group Name: {selectedChat.topic || "Unnamed Chat"}</h3>
          </div>
          <div
            ref={listRef}
            className="teams-chat-list overflow-y-auto overflow-x-hidden"
            style={{ height: '330px' }}
          >
            {messages.map((msg) => (
              <div
                key={msg.id}
                className="teams-chat-item bg-white rounded-lg shadow-sm p-3 mb-2 border border-gray-200 flex items-start gap-2"
              >
                <div className="teams-chat-avatar w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-sm font-medium text-blue-600 shrink-0">
                  {msg.sender
                    .split(" ")
                    .map((w) => w[0])
                    .join("")
                    .toUpperCase()}
                </div>
                <div className="teams-chat-item-body flex-1 min-w-0">
                  <div className="teams-chat-item-top flex items-center justify-between gap-2 mb-1">
                    <div className="teams-chat-sender-name text-sm font-medium truncate">
                      {msg.sender}
                    </div>
                    <div className="teams-chat-time text-xs text-gray-500 shrink-0">
                      {new Date(msg.created).toLocaleDateString([], {
                        month: "short",
                        day: "numeric",
                      })}{" "}
                      {new Date(msg.created).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                  <div
                    className="teams-chat-content text-sm text-gray-700"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(msg.content),
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="teams-chat-empty text-sm text-gray-500">
          <p>No existing Teams chat found for this project.</p>
        </div>
      )}
    </section>
  );
}

export default TeamsChat;
